/**
 * Paso 2 en iOS (T033; research R-5): instalar Fermin antes de registrar el dispositivo.
 *
 * En iOS, Safari y la PWA instalada **no comparten** almacenamiento ni cookies, y la app instalada
 * abre `start_url`, no el enlace de invitación. Por eso aquí se muestran las instrucciones y un
 * **código de traspaso** de 6 caracteres (TTL 15 min, un solo uso) que la app instalada canjea en
 * `/codigo`. El código se renderiza con `data-testid="handoff-code"` (lo lee el E2E T035).
 *
 * "Continuar en Safari" es una salida legítima: se advierte que instalar después obligará a pedir
 * una re-vinculación, y el dispositivo queda registrado con `display_mode = browser`. Va al final
 * del contenido, sin pie fijo (piloto 2026-09-30: el pie tapaba los pasos).
 *
 * Las instrucciones son la guía animada (`features/install`, cargada en diferido) de la variante
 * detectada —iOS 26 / iOS 18 / Chrome iOS / iPad— o la forzada con `?instalar=<variante>` (QA).
 */
import { Check, Clock, Copy, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../components/ui/card';
import { Screen } from '../../components/ui/screen';
import { Stepper } from '../../components/ui/stepper';
import { formatExpiryDayTime, t } from '../../i18n';
import { InstallGuideLazy } from '../install/install-guide-lazy';
import {
  APP_NAME,
  currentHost,
  detectDevice,
  forcedVariant,
  variantFor,
} from '../install/variants';

import { useHandoffCode } from './api';
import { isInvitationGone } from './link-invalid';

export interface InstallIosScreenProps {
  token: string;
  /** "Continuar en Safari": sigue a `/registrar` sin instalar. */
  onContinueInBrowser: () => void;
  /** La invitación dejó de ser válida (410). */
  onGone: () => void;
  /**
   * 005 · QA-6: el código de traspaso solo se emite con el documento verificado
   * (`428 DOCUMENT_VERIFICATION_REQUIRED`); vuelve al enlace a verificar.
   */
  onVerificationRequired?: () => void;
}

export function InstallIosScreen({
  token,
  onContinueInBrowser,
  onGone,
  onVerificationRequired,
}: InstallIosScreenProps) {
  const handoff = useHandoffCode(token);
  const device = useMemo(() => detectDevice(), []);
  const [variantId] = useState(() => forcedVariant() ?? variantFor(device));

  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(timer);
  }, [copied]);
  async function copyCode(code: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // Sin permiso de portapapeles: el código sigue a la vista para anotarlo.
      setCopied(false);
    }
  }

  const handoffError = handoff.error;
  useEffect(() => {
    if (isInvitationGone(handoffError)) {
      onGone();
      return;
    }
    if (handoffError?.is('DOCUMENT_VERIFICATION_REQUIRED')) {
      onVerificationRequired?.();
    }
  }, [handoffError, onGone, onVerificationRequired]);

  return (
    <Screen
      data-testid="activation-install"
      header={<Stepper current={2} />}
      title={t('activation.install.title')}
      lead={t('activation.install.lead')}
    >
      {/* Piloto 2026-09-30: el código va PRIMERO. Debajo de la animación pasaba inadvertido y, sin
          él, la app instalada no puede continuar el registro. */}
      <Card className="border-2 border-fm-primary">
        <CardTitle>{t('activation.install.codeTitle')}</CardTitle>
        <CardDescription>{t('activation.install.codeLead')}</CardDescription>
        {handoff.data ? (
          <>
            <p
              data-testid="handoff-code"
              className="fm-tabular mt-fm-3 text-center font-display text-fm-3xl font-extrabold tracking-[0.2em] text-fm-text"
            >
              {handoff.data.code}
            </p>
            <div className="mt-fm-3 flex justify-center">
              <Button
                variant="secondary"
                data-testid="handoff-copy"
                icon={
                  copied ? (
                    <Check aria-hidden="true" size={18} />
                  ) : (
                    <Copy aria-hidden="true" size={18} />
                  )
                }
                onClick={() => void copyCode(handoff.data.code)}
              >
                {copied ? t('activation.install.codeCopied') : t('activation.install.codeCopy')}
              </Button>
            </div>
            {/* Piloto 2026-09-30: el vencimiento en color de advertencia (`--fm-warning-text`,
                AA en claro y en oscuro; design-system §3), con icono para no depender del color. */}
            <p
              data-testid="handoff-expires"
              className="mt-fm-2 flex items-center justify-center gap-fm-1 text-center text-fm-sm font-semibold text-fm-warning-text"
            >
              <Clock aria-hidden="true" size={16} className="shrink-0" />
              {formatExpiryDayTime(handoff.data.expires_at)}
            </p>
          </>
        ) : (
          <p aria-live="polite" className="mt-fm-3 text-center text-fm-base text-fm-text-secondary">
            {handoff.isError ? t('activation.install.codeError') : t('common.states.loading')}
          </p>
        )}
      </Card>

      <h2 className="font-display text-fm-lg font-bold text-fm-text">
        {t('activation.install.guideTitle')}
      </h2>
      <InstallGuideLazy
        variantId={variantId}
        appName={APP_NAME}
        host={currentHost()}
        info={device}
      />

      <Card>
        <CardTitle>{t('activation.install.openTitle')}</CardTitle>
        <CardDescription>{t('activation.install.openLead')}</CardDescription>
      </Card>

      {/* Piloto 2026-09-30: sin pie fijo (tapaba los pasos) y sin "Ya lo instalé, ¿cómo sigo?"
          (el paso 3 ya lo explica). "Continuar en Safari" queda al final, como salida discreta. */}
      <section
        data-testid="install-safari-option"
        aria-labelledby="install-safari-title"
        className="mt-fm-4 flex flex-col gap-fm-2 border-t border-fm-border pt-fm-4"
      >
        <h2 id="install-safari-title" className="text-fm-sm font-semibold text-fm-text-secondary">
          {t('activation.install.safariTitle')}
        </h2>
        <Button variant="ghost" onClick={onContinueInBrowser}>
          {t('activation.install.safariAction')}
        </Button>
        <p className="flex items-start gap-fm-2 text-fm-sm text-fm-text-secondary">
          <TriangleAlert aria-hidden="true" size={16} className="mt-fm-1 shrink-0" />
          {t('activation.install.safariWarning')}
        </p>
      </section>
    </Screen>
  );
}
